import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const related = articles.filter((a) => a.slug !== slug && a.category === article?.category).slice(0, 3);

  if (!article) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center px-4">
          <i className="ri-file-unknow-line text-6xl text-foreground-300 mb-4 block"></i>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.date,
    inLanguage: 'zh-CN',
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanman.site/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://hanman.site/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://hanman.site/article/${article.slug}` },
    ],
  };

  return (
    <main>
      <SEOMeta
        title={`${article.title} | 韩漫导航`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://hanman.site/article/${article.slug}`}
        ogType="article"
      />
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: article.categoryName, to: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          {/* Cover */}
          <div className="relative w-full h-56 md:h-80 rounded-2xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4">
              <span className="px-3 py-1 bg-primary-500 text-white text-xs rounded-full whitespace-nowrap">
                {article.categoryName}
              </span>
            </div>
          </div>

          {/* Title */}
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-foreground-400"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-foreground-400"></i>
              阅读时长约 {article.readTime}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full text-xs whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Excerpt */}
          <p className="text-foreground-600 text-base leading-relaxed mb-8 p-4 bg-background-100 rounded-xl border-l-4 border-primary-400">
            {article.excerpt}
          </p>

          {/* Content */}
          <div
            className="prose prose-sm max-w-none text-foreground-800 leading-relaxed
              [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
              [&_p]:mb-4 [&_p]:text-foreground-700 [&_p]:leading-8
              [&_strong]:text-foreground-900 [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Back */}
          <div className="mt-12 pt-6 border-t border-background-200">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回 {article.categoryName}
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-6">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                  className="group bg-white rounded-xl overflow-hidden transition-all hover:-translate-y-1 duration-300 cursor-pointer"
                >
                  <div className="w-full h-32 overflow-hidden">
                    <img
                      src={rel.coverImage}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2">{rel.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}